import type { FunctionComponent } from 'react';
import styled, { css } from 'styled-components';

import type { BaseProps, ForwardProps, NoChildrenProp } from '../../types';
import { defaultThemeProp } from '../../theme';
import Icon, { registerIcon } from '../Icon';
import * as starIcon from '../Icon/icons/star.icon';
import * as starSolidIcon from '../Icon/icons/star-solid.icon';
import Flex from '../Flex';

registerIcon(starIcon, starSolidIcon);

export interface RatingProps extends BaseProps, NoChildrenProp {
  /**
   * Sets the rating to read only.
   * @default false
   */
  readOnly?: boolean;
  /**
   * The maximum rating allowed. Must be an integer greater than 1.
   * @default 5
   */
  maxRating?: number;
  /** The value of the Rating. Could be a decimal or integer. */
  value: number;
  /** Text that is displayed in parentheses inline with the Rating stars. */
  metaInfo?: string;
  /** Aria label for screen readers. */
  'aria-label'?: string;
  /**
   * The tabIndex to control focus.
   * @default 0
   */
  tabIndex?: number;
}

const DEFAULT_MAX_RATING = 5;

const defaultProps: Partial<RatingProps> = {
  readOnly: false,
  maxRating: DEFAULT_MAX_RATING,
  tabIndex: 0
};

const StyledRatingItemsContainer = styled.div(props => {
  const { theme } = props;
  return css`
    color: ${theme.components.rating.color};
    line-height: 1;
    vertical-align: middle;
    display: inline-block;
  `;
});

StyledRatingItemsContainer.defaultProps = defaultThemeProp;

const StyledPartialRatingContainer = styled.div`
  position: relative;
  display: inline-block;
`;

interface StyledPartialRatingProps {
  ratingPercentage: number;
}

export const StyledPartialRating = styled.div<StyledPartialRatingProps>(props => {
  const { ratingPercentage } = props;
  return css`
    display: inline-block;
    position: absolute;
    inset-inline-start: 0;
    width: ${ratingPercentage}%;
    overflow: hidden;
    white-space: nowrap;
  `;
});

const StyledRatingMetaInfo = styled.div(props => {
  const { theme } = props;
  return css`
    margin-inline-start: ${theme.base.spacing};
  `;
});

StyledRatingMetaInfo.defaultProps = defaultThemeProp;

interface RatingItemProps {
  fill: number;
}

const RatingItem: FunctionComponent<RatingItemProps> = (props: RatingItemProps) => {
  const { fill, ...restProps } = props;
  const fillPercentage = Math.round((100 / 1) * fill);
  return (
    <>
      {fillPercentage === 0 && <Icon {...restProps} name='star' />}
      {fillPercentage === 100 && <Icon {...restProps} name='star-solid' />}
      {fillPercentage > 0 && fillPercentage < 100 && (
        <StyledPartialRatingContainer>
          <Icon {...restProps} name='star' />
          <StyledPartialRating ratingPercentage={fillPercentage}>
            <Icon {...restProps} name='star-solid' />
          </StyledPartialRating>
        </StyledPartialRatingContainer>
      )}
    </>
  );
};

const Rating: FunctionComponent<RatingProps & ForwardProps> = (props: RatingProps) => {
  const starsOutline = [];
  const { 'aria-label': ariaLabel, metaInfo, tabIndex, readOnly, ...restProps } = props;

  let { maxRating, value } = props;
  maxRating = typeof maxRating !== 'number' ? DEFAULT_MAX_RATING : maxRating;
  maxRating = Math.max(1, maxRating);
  value = Math.min(maxRating, value);

  const isDecimalValue = Math.round(value) !== value;
  for (let i = 1; i <= maxRating; i += 1) {
    let fillValue = i <= value ? 1 : 0;
    if (isDecimalValue && Math.ceil(value) === i) {
      fillValue = value - Math.floor(value);
    }
    starsOutline.push(<RatingItem key={`star-${i}`} fill={fillValue} />);
  }

  return (
    <Flex
      container={{ alignItems: 'center' }}
      aria-label={ariaLabel}
      tabIndex={tabIndex}
      {...restProps}
    >
      <StyledRatingItemsContainer>{starsOutline}</StyledRatingItemsContainer>
      {metaInfo && <StyledRatingMetaInfo>({metaInfo})</StyledRatingMetaInfo>}{' '}
    </Flex>
  );
};

Rating.defaultProps = defaultProps;

export default Rating;
